import React from 'react';
import { Truck, Clock, DollarSign, FileText, ShieldCheck, ChevronRight } from 'lucide-react';
import Breadcrumbs from '../Navigation/Breadcrumbs';
import RequestForm from './RequestForm';

function LogisticsPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Gradient Background */}
      <section className="pt-32 pb-40 px-6 bg-gradient-to-b from-[#1B3BA0] to-[#0F2875] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
          <div className="absolute inset-0 bg-[#1B3BA0]/50 mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#1B3BA0]/20"></div>
        </div>
        <div className="max-w-[980px] mx-auto relative">
          <div className="mb-12">
            <Breadcrumbs />
          </div>
          <div className="max-w-[780px]">
            <h1 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight">
              <span className="text-white block mb-2">Логистика и таможенное</span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-[#99BBFF]">
                оформление грузов
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed">
              Надежная доставка грузов из Китая с оптимальным соотношением скорости и стоимости
            </p>
          </div>
        </div>
      </section>

      {/* CARGO Introduction */}
      <section className="py-24 px-6 bg-white relative z-10 -mt-20">
        <div className="max-w-[980px] mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl border border-gray-100">
            <h2 className="text-3xl font-bold text-[#1B3BA0] mb-6">
              Доставка CARGO
            </h2>
            <p className="text-lg text-gray-600 mb-12 leading-relaxed">
              Доставка в формате <span className="text-[#1B3BA0] font-semibold">CARGO</span> — популярный и проверенный способ ввоза товаров, которым пользуются более 95% клиентов. Это удобное решение для малого и среднего бизнеса.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: <Clock className="w-12 h-12 text-[#1B3BA0]" />,
                  title: 'Быстрая доставка',
                  description: 'От 10 до 25 дней в зависимости от тарифа'
                },
                {
                  icon: <DollarSign className="w-12 h-12 text-[#1B3BA0]" />,
                  title: 'Низкая стоимость',
                  description: 'Выгоднее официальной доставки'
                },
                {
                  icon: <FileText className="w-12 h-12 text-[#1B3BA0]" />,
                  title: 'Минимум документов',
                  description: 'Не требуется оформление со стороны клиента'
                },
                {
                  icon: <ShieldCheck className="w-12 h-12 text-[#1B3BA0]" />,
                  title: 'Безопасность',
                  description: 'Работаем только с надёжными каналами'
                }
              ].map((feature, index) => (
                <div key={index} className="bg-[#1B3BA0]/5 rounded-2xl p-8 hover:bg-[#1B3BA0]/10 transition-all duration-300 transform hover:-translate-y-1">
                  <div className="mb-6">{feature.icon}</div>
                  <h3 className="text-xl font-bold text-[#1B3BA0] mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Official Import */}
      <section className="py-32 px-6 bg-gradient-to-b from-white to-[#1B3BA0]/5">
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#1B3BA0] mb-16">
            Белая доставка (официальный ввоз)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
              <h3 className="text-2xl font-bold text-[#1B3BA0] mb-6">
                Преимущества
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <FileText className="w-5 h-5 text-[#1B3BA0] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-600">Полный пакет документов: ГТД, инвойс, упаковочный лист, сертификаты</span>
                </li>
                <li className="flex items-start">
                  <ShieldCheck className="w-5 h-5 text-[#1B3BA0] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-600">Подходит для компаний, которым требуется легальный учёт товара</span>
                </li>
                <li className="flex items-start">
                  <Truck className="w-5 h-5 text-[#1B3BA0] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-600">Возможность участия в тендерах и госзакупках</span>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
              <h3 className="text-2xl font-bold text-[#1B3BA0] mb-6">
                Особенности
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <DollarSign className="w-5 h-5 text-[#1B3BA0] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-600">Стоимость выше из-за таможенных пошлин и НДС</span>
                </li>
                <li className="flex items-start">
                  <Clock className="w-5 h-5 text-[#1B3BA0] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-600">
                    Сроки доставки:
                    <br />— Авто: от 10 до 25 дней
                    <br />— Морем: от 18 до 50 дней
                  </span>
                </li>
                <li className="flex items-start">
                  <FileText className="w-5 h-5 text-[#1B3BA0] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-600">Полное таможенное оформление и сертификация</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Help Section */}
      <section className="py-32 px-6 bg-[#1B3BA0]">
        <div className="max-w-[780px] mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Мы поможем выбрать оптимальный вариант
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Подберём логистику под ваши задачи — быстро и выгодно или официально и с документами
          </p>
          <RequestForm source="Логистика" />
        </div>
      </section>
    </div>
  );
}

export default LogisticsPage;